import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import './Toast.css';

const ToastContext = createContext(null);

let toastIdCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 200);
  }, []);

  const toast = useCallback((type, title, message, extra = null, duration = 4000) => {
    const id = ++toastIdCounter;
    setToasts(prev => [...prev, { id, type, title, message, extra, exiting: false }]);
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  const toastFn = useCallback((title, message, extra) => toast('info', title, message, extra), [toast]);
  toastFn.success = (title, message, extra) => toast('success', title, message, extra);
  toastFn.error = (title, message, extra) => toast('error', title, message, extra);
  toastFn.warning = (title, message, extra) => toast('warning', title, message, extra);
  toastFn.info = (title, message, extra) => toast('info', title, message, extra);

  const icons = {
    success: 'fa-solid fa-circle-check',
    error: 'fa-solid fa-circle-xmark',
    warning: 'fa-solid fa-triangle-exclamation',
    info: 'fa-solid fa-circle-info',
  };
  const ticketTypes = ['ticket_reply','ticket_admin_reply','ticket_new','ticket_admin_created','ticket_status'];

  return (
    <ToastContext.Provider value={toastFn}>
      {children}
      <div className="toast-container">
        {toasts.map(t => {
          const ex = t.extra || {};
          const isTicket = ticketTypes.includes(ex.notifType);
          const metaParts = [];
          if (ex.ticketId) metaParts.push(`Ticket #${ex.ticketId}`);
          if (ex.domain) metaParts.push(ex.domain);
          if (ex.userEmail && ex.userEmail !== ex.senderName) metaParts.push(ex.userEmail);
          return (
            <div key={t.id} className={`toast toast--${t.type}${t.exiting ? ' toast-exit' : ''}${isTicket ? ' toast--rich' : ''}`}>
              {isTicket ? (
                <>
                  <div className="toast-rich-header">
                    <span style={{fontSize:'11px',color:'var(--admin-text-muted)',fontWeight:500,textTransform:'uppercase',letterSpacing:'0.04em'}}>OpenHosst</span>
                    <button className="toast-close" onClick={() => removeToast(t.id)}>&times;</button>
                  </div>
                  <div className="toast-rich-body">
                    <div className="toast-rich-icon"><i className="fa-solid fa-headset" /></div>
                    <div className="toast-rich-content">
                      <div className="toast-title">{t.title}</div>
                      {ex.senderName && <div className="toast-rich-from">From: {ex.senderName}</div>}
                      {metaParts.length > 0 && <div className="toast-rich-meta">{metaParts.join(' · ')}</div>}
                      {t.message && <div className="toast-rich-preview">"{t.message}"</div>}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <span className="toast-icon"><i className={icons[t.type]} /></span>
                  <div className="toast-content">
                    <div className="toast-title">{t.title}</div>
                    {t.message && <div className="toast-message">{t.message}</div>}
                  </div>
                  <button className="toast-close" onClick={() => removeToast(t.id)}>&times;</button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}

export default ToastProvider;
